import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import { useServer } from '../contexts/ServerContext';
import QRCode from 'qrcode';

// 创建全局 socket 实例
let globalSocket = null;

// 创建 socket 实例的函数
const createSocket = (serverUrl) => {
  if (globalSocket) {
    return globalSocket;
  }
  
  globalSocket = io(serverUrl, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000
  });
  
  return globalSocket;
};

function Subscriber() {
  const { serverUrl, isDebugMode } = useServer();
  const [socket, setSocket] = useState(null);
  const [qrCode, setQrCode] = useState('');
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [subscriptionCode, setSubscriptionCode] = useState('');
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState('');
  const [feedback, setFeedback] = useState('请输入订阅码开始订阅');
  const [isLocalMode, setIsLocalMode] = useState(false);
  const [localQrCode, setLocalQrCode] = useState('');
  const [localQrCodeImage, setLocalQrCodeImage] = useState('');
  const { code } = useParams();
  const navigate = useNavigate();
  const [isDebugPanelOpen, setIsDebugPanelOpen] = useState(false);
  const [currentCodeInfo, setCurrentCodeInfo] = useState(null);
  const [lastRemoteUpdate, setLastRemoteUpdate] = useState(null);
  const [currentMilliseconds, setCurrentMilliseconds] = useState('000');
  const [showNewCodeAlert, setShowNewCodeAlert] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  // 检查是否是特定格式的码
  const isCheckworkFormat = (code) => {
    return /^checkwork\|id=\d+&siteId=\d+&createTime=\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}&classLessonId=\d+$/.test(code);
  };

  // 从码中提取关键信息
  const extractCodeInfo = (code) => {
    if (!isCheckworkFormat(code)) return null;
    const match = code.match(/id=(\d+)&siteId=(\d+)&createTime=.*?\.(\d{3})&classLessonId=(\d+)/);
    if (!match) return null;
    return {
      id: match[1],
      siteId: match[2],
      milliseconds: match[3],
      classLessonId: match[4]
    };
  };

  // 检查是否是新的码
  const isNewCode = (code) => {
    const newInfo = extractCodeInfo(code);
    if (!newInfo || !currentCodeInfo) return true;
    return newInfo.id !== currentCodeInfo.id || 
           newInfo.siteId !== currentCodeInfo.siteId || 
           newInfo.classLessonId !== currentCodeInfo.classLessonId;
  };

  // 生成本地动态码
  const generateLocalCode = () => {
    // 获取当前本地时间（已经是本地时区了）
    const now = new Date();
    
    // 使用本地时间计算秒数
    const currentSeconds = now.getSeconds();
    const adjustedSeconds = Math.floor(currentSeconds / 5) * 5;
    const formattedSeconds = adjustedSeconds.toString().padStart(2, '0');
    
    // 构建新的时间字符串（使用本地时间）
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    
    const timeStr = `${year}-${month}-${day}T${hours}:${minutes}:${formattedSeconds}.${currentMilliseconds}`;
    
    // 使用currentCodeInfo构建新码
    const newCode = `checkwork|id=${currentCodeInfo.id}&siteId=${currentCodeInfo.siteId}&createTime=${timeStr}&classLessonId=${currentCodeInfo.classLessonId}`;
    
    setDebugInfo(prev => prev + `\n[更新] 生成本地码，新时间: ${timeStr}`);
    return newCode;
  };

  // 处理本地动态码更新
  useEffect(() => {
    if (!currentCodeInfo) return;

    const updateLocalCode = async () => {
      try {
        const newCode = generateLocalCode();
        if (!newCode) {
          throw new Error('生成的码为空');
        }
        
        // 只有当新码与当前码不同时才更新
        if (newCode !== localQrCode) {
          setLocalQrCode(newCode);
          setDebugInfo(prev => prev + `\n[更新] 本地码已更新: ${newCode}`);
          
          const qrImageUrl = await QRCode.toDataURL(newCode, {
            width: 300,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#ffffff'
            }
          });
          setLocalQrCodeImage(qrImageUrl);
        }
      } catch (err) {
        setDebugInfo(prev => prev + '\n[错误] 生成本地二维码图片失败: ' + err.message);
      }
    };

    // 计算距离下一个5秒的时间
    const now = new Date();
    const currentSeconds = now.getSeconds();
    const currentMilliseconds = now.getMilliseconds();
    const nextFiveSeconds = Math.ceil(currentSeconds / 5) * 5;
    const delay = (nextFiveSeconds - currentSeconds) * 1000 - currentMilliseconds;

    // 立即执行一次更新
    updateLocalCode();
    
    // 等待到下一个5秒的整数时刻
    const initialTimeout = setTimeout(() => {
      updateLocalCode();
      // 之后每5秒更新一次
      const interval = setInterval(updateLocalCode, 5000);
      // 清理函数中清除两个定时器
      return () => {
        clearInterval(interval);
        clearTimeout(initialTimeout);
      };
    }, delay);

    // 清理函数
    return () => {
      clearTimeout(initialTimeout);
    };
  }, [currentCodeInfo, currentMilliseconds]);

  // 初始化 socket 连接
  useEffect(() => {
    // 如果存在旧的连接，先断开
    if (globalSocket) {
      globalSocket.disconnect();
      globalSocket = null;
    }
    
    const newSocket = createSocket(serverUrl);
    setSocket(newSocket);

    return () => {
      // 只在组件真正卸载时断开连接
      if (globalSocket) {
        globalSocket.disconnect();
        globalSocket = null;
      }
    };
  }, [serverUrl]);

  // 处理订阅码
  useEffect(() => {
    if (code && socket) {
      setSubscriptionCode(code);
      setFeedback(`正在订阅：${code}`);
      setDebugInfo(prev => prev + '\n[连接] 正在订阅发布者: ' + code);
      socket.emit('subscribe', code);
    }
  }, [code, socket]);

  // Socket.IO 事件监听
  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setDebugInfo(prev => prev + '\n[连接] Socket.IO 已连接');
      // 只在有有效的订阅码时才重新订阅
      if (subscriptionCode) {
        setFeedback(`正在订阅：${subscriptionCode}`);
        setDebugInfo(prev => prev + '\n[连接] 重新订阅发布者: ' + subscriptionCode);
        socket.emit('subscribe', subscriptionCode);
      }
    };

    const handleDisconnect = () => {
      setDebugInfo(prev => prev + '\n[连接] Socket.IO 已断开');
      setError('与服务器断开连接，请刷新页面重试');
      setFeedback('连接已断开，请刷新页面重试');
    };

    const handleQrCodeUpdate = async (newQrCode) => {
      const now = new Date();
      setLastRemoteUpdate(now);
      const timestamp = now.toLocaleTimeString();
      
      setQrCode(newQrCode);
      setDebugInfo(prev => prev + `\n[${timestamp}] 收到新的二维码: ${newQrCode}`);
      setError(''); // 清除可能的错误信息
      
      // 检查是否是特定格式的码
      if (isCheckworkFormat(newQrCode)) {
        const newInfo = extractCodeInfo(newQrCode);
        if (newInfo) {
          // 如果是新的码或者关键字段有变化，更新本地码
          if (!currentCodeInfo || 
              newInfo.id !== currentCodeInfo.id || 
              newInfo.siteId !== currentCodeInfo.siteId || 
              newInfo.classLessonId !== currentCodeInfo.classLessonId) {
            setDebugInfo(prev => prev + '\n[检测] 发现字段变化，更新本地码');
            setDebugInfo(prev => prev + `\n[对比] 旧ID: ${currentCodeInfo?.id} -> 新ID: ${newInfo.id}`);
            setDebugInfo(prev => prev + `\n[对比] 旧siteId: ${currentCodeInfo?.siteId} -> 新siteId: ${newInfo.siteId}`);
            setDebugInfo(prev => prev + `\n[对比] 旧classLessonId: ${currentCodeInfo?.classLessonId} -> 新classLessonId: ${newInfo.classLessonId}`);
            setDebugInfo(prev => prev + `\n[对比] 旧毫秒: ${currentMilliseconds} -> 新毫秒: ${newInfo.milliseconds}`);
            
            setCurrentCodeInfo(newInfo);
            setCurrentMilliseconds(newInfo.milliseconds);
            setLocalQrCode(newQrCode);
            setIsLocalMode(true);
            
            // 显示新码提示
            setShowNewCodeAlert(true);
          }
        }
      }
      
      // 生成二维码图片
      try {
        if (!newQrCode) {
          throw new Error('收到的码为空');
        }
        
        const qrImageUrl = await QRCode.toDataURL(newQrCode, {
          width: 300,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        setQrCodeImage(qrImageUrl);
        setDebugInfo(prev => prev + '\n[生成] 二维码图片已生成');
      } catch (err) {
        setDebugInfo(prev => prev + '\n[错误] 生成二维码图片失败: ' + err.message);
      }
    };

    const handleError = (message) => {
      setError(message);
      setFeedback('订阅失败，请检查订阅码是否正确');
      setDebugInfo(prev => prev + '\n[错误] ' + message);
      // 如果是无效的发布者码，清除订阅码
      if (message.includes('无效的发布者码')) {
        setSubscriptionCode('');
        setQrCode('');
        setQrCodeImage('');
      }
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('qrCodeUpdate', handleQrCodeUpdate);
    socket.on('error', handleError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('qrCodeUpdate', handleQrCodeUpdate);
      socket.off('error', handleError);
    };
  }, [socket, subscriptionCode, currentCodeInfo, currentMilliseconds]);

  // 处理提示显示
  useEffect(() => {
    if (showNewCodeAlert) {
      setIsAlertVisible(true);
      const timer = setTimeout(() => {
        setIsAlertVisible(false);
        setTimeout(() => setShowNewCodeAlert(false), 500); // 等动画结束后再移除DOM
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNewCodeAlert]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (subscriptionCode.length !== 6) {
      setError('订阅码必须是6个字符');
      setFeedback('请输入正确的6位订阅码');
      return;
    }
    // 检查是否包含非法字符
    if (!/^[A-Za-z0-9_-]{6}$/.test(subscriptionCode)) {
      setError('订阅码只能包含字母、数字、下划线和连字符');
      setFeedback('请输入有效的订阅码');
      return;
    }
    setError(''); // 清除之前的错误
    setQrCode(''); // 清除之前的二维码
    setQrCodeImage(''); // 清除之前的二维码图片
    setFeedback(`正在订阅：${subscriptionCode}`);
    setDebugInfo(prev => prev + '\n[连接] 正在订阅发布者: ' + subscriptionCode);
    socket.emit('subscribe', subscriptionCode);
    navigate(`/subscribe/${subscriptionCode}`);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-center mb-12">订阅模式</h1>
      
      {/* 新码提示 */}
      {showNewCodeAlert && (
        <div 
          className={`fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg transform transition-all duration-500 ease-in-out ${
            isAlertVisible 
              ? 'translate-x-0 opacity-100' 
              : 'translate-x-full opacity-0'
          }`}
        >
          <div className="flex items-center">
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>收到新的有效远程码，本地码已自动更新</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              输入订阅码
            </label>
            <input
              type="text"
              value={subscriptionCode}
              onChange={(e) => setSubscriptionCode(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="请输入6位订阅码"
              maxLength={6}
              pattern="[A-Za-z0-9_-]{6}"
              title="请输入6位字符（字母、数字、下划线或连字符）"
            />
            <p className="mt-2 text-sm text-gray-500">
              请输入发布者提供的6位订阅码
            </p>
          </div>
          <button
            type="submit"
            className="w-full bg-blue-500 text-white py-3 rounded-lg hover:bg-blue-600 transition-colors"
          >
            开始订阅
          </button>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="text-center text-gray-600 mt-4">
          {feedback}
        </div>

        {/* 本地模式开关 - 只在有currentCodeInfo时显示 */}
        {currentCodeInfo && (
          <div className="mt-4 text-center">
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isLocalMode}
                onChange={(e) => setIsLocalMode(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              <span className="ml-3 text-sm font-medium text-gray-900">启用本地动态码</span>
            </label>
          </div>
        )}

        {qrCode && (
          <div className="mt-8 text-center">
            <h2 className="text-xl font-semibold mb-4">远程动态码</h2>
            <div className="bg-gray-100 p-4 rounded-lg">
              {qrCodeImage ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code" 
                    className="w-64 h-64 object-contain"
                  />
                  {isDebugMode && (
                    <p className="mt-4 font-mono text-sm break-all">{qrCode}</p>
                  )}
                  <p className="mt-2 text-sm text-gray-500">
                    最后更新: {lastRemoteUpdate ? lastRemoteUpdate.toLocaleTimeString() : '未接收到更新'}
                  </p>
                </div>
              ) : (
                <p className="font-mono text-2xl break-all">{qrCode}</p>
              )}
            </div>
          </div>
        )}

        {/* 本地动态码显示 */}
        {isLocalMode && localQrCode && currentCodeInfo && (
          <div className="mt-8 text-center">
            <h2 className="text-xl font-semibold mb-4">本地动态码</h2>
            <div className="bg-blue-100 p-4 rounded-lg">
              {localQrCodeImage ? (
                <div className="flex flex-col items-center">
                  <img 
                    src={localQrCodeImage} 
                    alt="Local QR Code" 
                    className="w-64 h-64 object-contain"
                  />
                  {isDebugMode && (
                    <>
                      <p className="mt-4 font-mono text-sm break-all">{localQrCode}</p>
                    </>
                  )}
                  <p className="mt-2 text-sm text-blue-600">
                    每5秒自动更新
                  </p>
                </div>
              ) : (
                <p className="font-mono text-2xl break-all">{localQrCode}</p>
              )}
            </div>
            <div className="mt-4 text-sm text-gray-600 max-w-lg mx-auto">
              <p className="mb-2">
                本地动态码会在每个5秒的整数时刻（如00、05、10秒）自动更新时间戳，其他字段（包括毫秒）保持与最新的远程动态码一致。
              </p>
              <p>
                即使远程连接断开或远程码发生变化，本地动态码也会持续更新，直到收到新的远程动态码。
              </p>
              <p>
                在本地动态码可用的情况下，推荐使用本地动态码。
              </p>
            </div>
          </div>
        )}

        {/* 调试信息面板 - 只在调试模式开启时显示 */}
        {isDebugMode && (
          <div className="mt-4">
            <button
              onClick={() => setIsDebugPanelOpen(!isDebugPanelOpen)}
              className="w-full flex items-center justify-between p-4 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h2 className="text-xl font-semibold">调试信息</h2>
              <span className="transform transition-transform duration-200" style={{ transform: isDebugPanelOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                ▼
              </span>
            </button>
            {isDebugPanelOpen && (
              <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm max-h-60 overflow-y-auto">
                  {debugInfo}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Subscriber; 